/*
 *
 * MerchantLayout
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 25-Nov-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import PortalLoader from '@nexidigital/portalloader'
import './MerchantLayout.scss'
import { SubrouteHeaderProps } from 'Components/SubrouteHeader/SubrouteHeader'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import DesktopNavBar from 'Components/DesktopNavBar'
import Footer from 'Components/Footer'
import DefaultHeader from 'Components/RouteHeader'
import SubrouteHeader from 'Components/SubrouteHeader'

const MerchantLayout = ({
  className,
  children,
  loading,
  header,
  footer,
  headerProps,
  hideHeader,
  hideFooter,
  hideSidebar,
  hideGutters,
  withGutters,
  noAuth
}) => {
  if (loading) {
    return <div className={`${noAuth ? 'noAuth' : ''}`}><PortalLoader centered /></div>
  }

  const { isSmallScreen } = useMobileDetect()

  const isSidebarHidden = hideSidebar || isSmallScreen

  const headerReducer = (header, props) => {
    const headerMap = {
      default: <DefaultHeader {...props} />,
      subroute: <SubrouteHeader {...props} />,
      wizard: <SubrouteHeader {...props} />
    }
    return headerMap[header] || headerMap.default
  }

  const footerReducer = (footer) => {
    const footerMap = {
      default: <Footer />
    }
    return footerMap[footer] || footerMap.default
  }

  return (
    <Box
      className={[
        'merchantLayout',
        ...(hideHeader ? ['hideHeader'] : []),
        ...(isSidebarHidden ? ['hideSidebar'] : []),
        ...(hideFooter ? ['hideFooter'] : []),
        ...(noAuth ? ['noAuth'] : []),
        className
      ].join(' ')}>
      {!hideHeader && (
        <Box
          className={`merchantLayoutHeaderWrapper ${['subroute', 'wizard'].includes(header) ? 'stickyHeader' : ''}`}>
          {headerReducer(header, headerProps)}
        </Box>
      )}
      {!isSidebarHidden && (
        <aside className='merchantLayoutSidebar'>
          <DesktopNavBar />
        </aside>
      )}
      <main className={`merchantLayoutMain ${withGutters ? 'withGutters' : ''} ${hideGutters ? 'hideGutters' : ''}`}>
        {children}
      </main>
      {!hideFooter && (
        <Box component='footer' className="merchantLayoutFooter">
          {footerReducer(footer)}
        </Box>
      )}
    </Box>
  )
}

MerchantLayout.propTypes = {
  /** Add className to the component */
  className: PropTypes.string,
  /** children nodes */
  children: PropTypes.node.isRequired,
  /** toggle render loader */
  loading: PropTypes.bool,
  /** header variant */
  header: PropTypes.oneOf(['default', 'subroute', 'wizard']),
  /** footer variant */
  footer: PropTypes.oneOf(['default']),
  /** header component props */
  headerProps: PropTypes.oneOfType([PropTypes.shape(SubrouteHeaderProps)]),
  /** hide header */
  hideHeader: PropTypes.bool,
  /** hide footer */
  hideFooter: PropTypes.bool,
  /** hide sidebar */
  hideSidebar: PropTypes.bool,
  /** remove main ontainer styling */
  hideGutters: PropTypes.bool,
  /** add gutters to main container */
  withGutters: PropTypes.bool,
  /** no authentication */
  noAuth: PropTypes.bool
}

MerchantLayout.defaultProps = {
  // Add here some default propTypes values
  className: '',
  localLoading: false,
  header: 'default',
  footer: 'default',
  hideHeader: false,
  hideSidebar: false,
  hideFooter: false,
  hideGutters: false,
  withGutters: false,
  noAuth: false
}

export default MerchantLayout
